import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { PERSONA_CARDS } from '@/data/personas';
import { login, getDemoPassword, setDemoPassword } from '@/api/persist';
import { signInAsCard } from '@/domain/signIn';

// "Monday 04 May 2026 at 1:00pm" in studio time — the same instant the header
// clock shows, so the splash never disagrees with the app about "now".
const SPLASH_PARTS = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
function splashClockLabel(iso: string): string {
  const p: Record<string, string> = {};
  for (const part of SPLASH_PARTS.formatToParts(new Date(iso))) p[part.type] = part.value;
  return `${p.weekday} ${p.day} ${p.month} ${p.year} at ${p.hour}:${p.minute}${(p.dayPeriod ?? '').toLowerCase()}`;
}

export default function PersonaSelector() {
  const navigate = useNavigate();
  const setPersona = useSessionStore((s) => s.setPersona);
  const loadFixtures = useBrainStore((s) => s.loadFixtures);
  const initialised = useBrainStore((s) => s.initialised);
  const clockIso = useSimulationStore((s) => s.clockIso);

  // One shared password unlocks the platform; once accepted it is held in
  // localStorage (shared across tabs) so persona clicks — and freshly opened
  // tabs — reuse it without re-prompting. The server
  // decides what counts as correct — when DEMO_LOGIN_PASSWORD is unset it
  // accepts anything, so local dev is never blocked.
  const [unlocked, setUnlocked] = useState(!!getDemoPassword());
  const [entered, setEntered] = useState('');
  const [pwError, setPwError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!initialised) loadFixtures();
  }, [initialised, loadFixtures]);

  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return; // ignore repeated Enter while a login is in flight
    setPwError(false);
    setSubmitting(true);
    try {
      const ok = await login('georgina', undefined, entered, { pid: 'georgina', name: 'Georgina Gray' });
      if (ok) {
        setDemoPassword(entered);
        setUnlocked(true);
      } else {
        // login() returns false for both a wrong password and an unreachable
        // backend, so keep the copy generic rather than asserting "wrong".
        setPwError(true);
        setEntered('');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!unlocked) {
    return (
      <div className="min-h-screen w-full bg-ink-black text-cream flex flex-col items-center justify-center px-6">
        <img
          src="/logo/tattoo_vault_wordmark.png"
          alt="Tattoo Vault"
          className="h-16 w-auto object-contain mb-10"
          data-testid="persona-wordmark"
        />
        <form
          onSubmit={handleUnlock}
          className="w-full max-w-sm flex flex-col gap-4 bg-ink-deep border border-gold-dim/30 rounded-2xl p-8"
          data-testid="unlock-gate"
        >
          <label htmlFor="unlock-password" className="label-small-caps text-gold-bright">
            Enter the shared password
          </label>
          <input
            id="unlock-password"
            type="password"
            value={entered}
            onChange={(ev) => setEntered(ev.target.value)}
            disabled={submitting}
            autoFocus
            data-testid="unlock-password"
            className="bg-ink-black border border-gold-dim/40 rounded-xl px-4 py-3 text-cream font-inter focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="unlock-submit"
            className="bg-gold-bright text-ink-black font-inter font-semibold rounded-xl px-4 py-3 hover:shadow-gold-glow transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright disabled:opacity-60"
          >
            {submitting ? 'Unlocking…' : 'Unlock'}
          </button>
          <p
            className="font-inter text-sm text-red-400 min-h-[1.25rem]"
            data-testid="unlock-error"
            role="alert"
          >
            {pwError ? 'Couldn’t unlock — check the password and try again.' : ''}
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-ink-black text-cream flex flex-col">
      <header className="px-12 pt-16 pb-10 flex flex-col gap-2 max-w-6xl mx-auto w-full">
        <img
          src="/logo/tattoo_vault_wordmark.png"
          alt="Tattoo Vault"
          className="h-20 w-auto self-start object-contain"
          data-testid="persona-wordmark"
        />
        <h1 className="heading-display text-4xl md:text-6xl text-gold-bright">
          A working prototype.
        </h1>
        <p className="font-serif text-lg text-cream-soft mt-3">
          Chaos out. Clarity in. Pick a seat at the table and step into Monday morning.
        </p>
      </header>

      <main className="px-12 pb-16 grow">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PERSONA_CARDS.filter((card) => !card.hidden).map((card, idx) => (
            <motion.button
              key={card.persona + idx}
              data-testid={`persona-card-${card.persona}`}
              type="button"
              onClick={() => {
                // Mint + store a token naming the persona and the person (B3); writes
                // queued before it lands wait for it (persist.ts afterSignIn).
                void signInAsCard(card);
                setPersona(card.persona, {
                  agentId: card.agentId,
                  artistId: card.artistId,
                  apprenticeId: card.apprenticeId,
                  assignedStudios: card.assignedStudios,
                  activeStudio: card.activeStudio,
                });
                navigate(card.destination);
              }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05, duration: 0.3 }}
              whileHover={{ y: -4 }}
              className="text-left bg-ink-deep border border-gold-dim/30 rounded-2xl p-7 hover:border-gold-bright hover:shadow-gold-glow transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright"
            >
              <h2 className="heading-serif text-2xl text-cream">{card.role}</h2>
              <p className="label-small-caps text-gold-bright mt-2">{card.name}</p>
              <p className="font-inter text-sm text-cream-soft leading-relaxed mt-4">
                {card.description}
              </p>
              <p className="mt-6 font-inter text-xs text-gold uppercase tracking-widest">
                Begin at {card.defaultHome}
              </p>
            </motion.button>
          ))}
        </div>
      </main>

      <footer className="px-12 pb-8 max-w-6xl mx-auto w-full flex items-end justify-between gap-4">
        <div className="grow">
          <div className="divider-gold mb-6" />
          <p className="font-inter text-xs text-cream-soft/70 tracking-wider uppercase">
            Simulated. Mock-everything. Stripe in test mode only.{' '}
            <span data-testid="persona-sim-now">{splashClockLabel(clockIso)}</span>.
          </p>
        </div>
        <a
          href="/console"
          data-testid="persona-open-console"
          className="shrink-0 font-inter text-[11px] uppercase tracking-widest text-cream-soft/70 hover:text-gold-bright underline"
        >
          Customer Simulator
        </a>
      </footer>
    </div>
  );
}
